export type DynamicImageLibraryText = {
	/** File Manager alt, with title / file name only when alt is blank. */
	alt: string | null;
	/** File Manager `title` (not the image element's Title field). */
	fileTitle: string | null;
};

export function isDynamicImageBinding(props: {
	imageBinding?: unknown;
	useFeaturedImage?: unknown;
}): boolean {
	const binding = props.imageBinding;
	if (binding === "featured" || binding === "author" || binding === "user") {
		return true;
	}
	return props.useFeaturedImage === true;
}

/**
 * Dynamic images change per entry. Their accessible name comes from the
 * resolved File Manager row, not the title/alt stored on the image element.
 */
export function assignDynamicImageLibraryText(
	props: Record<string, unknown>,
	library: DynamicImageLibraryText
): void {
	props.alt = library.alt ?? "";
	props.title = library.fileTitle ?? "";
}

function trimmedLibraryText(libraryValue: string | null | undefined): string {
	return typeof libraryValue === "string" ? libraryValue.trim() : "";
}

/** Non-empty element text wins. Otherwise the File Manager fallback. */
export function resolveStaticImageField(
	elementValue: unknown,
	libraryValue: string | null | undefined
): string {
	if (typeof elementValue === "string" && elementValue.trim()) {
		return elementValue.trim();
	}
	return trimmedLibraryText(libraryValue);
}

/** Stored override as typed. Library text only when the element field is empty. */
export function staticImageInspectorValue(
	elementValue: string | null | undefined,
	libraryValue: string | null | undefined
): string {
	if (typeof elementValue === "string" && elementValue.trim()) {
		return elementValue;
	}
	return trimmedLibraryText(libraryValue);
}

/** Keeps a stored static title/alt. Fills an empty field from the file fallback. */
export function assignStaticImageTextFallback(
	props: Record<string, unknown>,
	library: { alt: string | null; title: string | null }
): void {
	const currentAlt = typeof props.alt === "string" ? props.alt.trim() : "";
	if (!currentAlt) {
		const alt = resolveStaticImageField(props.alt, library.alt);
		if (alt) {
			props.alt = alt;
		}
	}
	const currentTitle =
		typeof props.title === "string" ? props.title.trim() : "";
	if (!currentTitle) {
		const title = resolveStaticImageField(props.title, library.title);
		if (title) {
			props.title = title;
		}
	}
}
